import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, Form, FormikValues } from 'formik';
import * as yup from 'yup';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import { Desktop, Mobile } from 'components/Responsive';
import ResponsiveInput from 'components/ResponsiveInput';
import ScreenHeader from 'components/ScreenHeader';
import useStoreTranslation from 'hooks/useStoreTranslation';

import {
  Container,
  Header,
  FieldsContainer,
  Checkbox,
  TermsContainer,
  TermsTitle,
  BackButton,
  NextButton,
  ButtonsContainer,
  ButtonLine,
} from '../../styles';
import { CountryValues } from '../Country';
import Privacy from './documents/privacy';
import Terms from './documents/terms';
import { DocumentTextContainer } from './styles';

export interface CompanyValues {
  name: string;
  document: string;
}

interface Props {
  values: CompanyValues | null;
  onSubmit: (values: CompanyValues) => void;
  country: CountryValues | null;
}

const DOCUMENT_MASK: { [key: string]: string } = {
  CNPJ: '99.999.999/9999-99',
  CUIT: '99-99999999-9',
  NIT: '999999999-9',
  RFC: '',
};

const Company = ({ values, onSubmit, country: countryValues }: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();

  const [termsChecked, setTermsChecked] = useState(values !== null);
  const [privacyChecked, setPrivacyChecked] = useState(values !== null);

  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  if (!countryValues) {
    return null;
  }

  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .required()
      .matches(/^[a-zA-Z0-9 _ \u00C0-\u024F\u1E00-\u1EFF]+$/g, {
        message: t('registrationOnboarding.company.errorMessageSpecial'),
      }),
    document: yup.string().companyDocument(countryValues?.country.documentType).required(),
  });

  const initialValues: CompanyValues = {
    name: '',
    document: '',
  };

  const openTerms = () => setShowTerms(true);
  const closeTerms = () => setShowTerms(false);

  const openPrivacy = () => setShowPrivacy(true);
  const closePrivacy = () => setShowPrivacy(false);

  const handleToggleTerms = () => setTermsChecked((terms) => !terms);
  const handleTogglePrivacy = () => setPrivacyChecked((privacy) => !privacy);

  const handleBack = () => history.goBack();
  const handleSubmit = (formValues: CompanyValues) => onSubmit(formValues);

  return (
    <>
      <Formik
        initialStatus={{ rightCheckIfIsValid: true }}
        initialValues={values || initialValues}
        isInitialValid={(formik: any) => validationSchema.isValidSync(formik.initialValues)}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid }: FormikValues) => (
          <Form>
            <Container>
              <Header>{t('registrationOnboarding.company.title')}</Header>

              <FieldsContainer>
                <ResponsiveInput name="name" placeholder={t('registrationOnboarding.company.companyPlaceholder')} />

                <ResponsiveInput
                  name="document"
                  placeholder={countryValues?.country.documentType}
                  mask={DOCUMENT_MASK[countryValues?.country.documentType] || ''}
                />
              </FieldsContainer>

              <TermsContainer>
                <TermsTitle>{t('registrationOnboarding.company.acceptTerms.title')}</TermsTitle>
                <Checkbox checked={termsChecked} onChange={handleToggleTerms} circle size={24}>
                  <div id="terms">
                    &nbsp;
                    <a href="https://travel.rappi.com.co/terms" target="_blank" rel="noreferrer">
                      {t('registrationOnboarding.company.acceptTerms.terms')}
                    </a>
                  </div>
                </Checkbox>

                <Checkbox checked={privacyChecked} onChange={handleTogglePrivacy} circle size={24}>
                  <div id="terms">
                    &nbsp;{' '}
                    <a
                      href="https://legal.rappi.com/colombia/politica-de-proteccion-y-tratamiento-de-datos-personales-rappi-s-a-s/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('registrationOnboarding.company.acceptTerms.privacy')}
                    </a>
                  </div>
                </Checkbox>
              </TermsContainer>

              <ButtonsContainer>
                <ButtonLine>
                  <BackButton type="button" onClick={handleBack}>
                    {t('registrationOnboarding.backButton')}
                  </BackButton>
                  <NextButton disabled={!termsChecked || !privacyChecked || !isValid}>
                    {t('registrationOnboarding.nextButton')}
                  </NextButton>
                </ButtonLine>
              </ButtonsContainer>
            </Container>
          </Form>
        )}
      </Formik>

      <ApplicationOverlayContent sidebar onClose={closeTerms} width={1000} show={showTerms}>
        <>
          <Mobile>
            <AppBar noGoBack rightIcon="/images/close.svg" rightAction={closeTerms}>
              {t('registrationOnboarding.company.termsTitle')}
            </AppBar>
          </Mobile>
          <Desktop>
            <ScreenHeader title={t('registrationOnboarding.company.termsTitle')} />
          </Desktop>
          <DocumentTextContainer>
            <Terms country={countryValues?.country.id} />
          </DocumentTextContainer>
        </>
      </ApplicationOverlayContent>

      <ApplicationOverlayContent sidebar onClose={closePrivacy} width={1000} show={showPrivacy}>
        <>
          <Mobile>
            <AppBar noGoBack rightIcon="/images/close.svg" rightAction={closePrivacy}>
              {t('registrationOnboarding.company.privacyTitle')}
            </AppBar>
          </Mobile>
          <Desktop>
            <ScreenHeader title={t('registrationOnboarding.company.privacyTitle')} />
          </Desktop>
          <DocumentTextContainer>
            <Privacy country={countryValues?.country.id} />
          </DocumentTextContainer>
        </>
      </ApplicationOverlayContent>
    </>
  );
};

export default Company;
